import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import { Formik, FormikProps } from 'formik';
import SubmitButton from './SubmitButton';
import Label from './Label';
import AddressLookup, { Address } from './AddressLookup';
import DayPickerInputValue from './edit/DayPickerInputValue';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { useDateDisabler } from '../utilities/DateDisabler';
import { axiosKK } from '../services/networkRequest';
import {
  OrderBoxesResponse,
  BackendShippingAddress,
  CreateDPDLabelRequest,
  CreateDPDLabelResponse,
  DPDLabel,
  DPDService,
  ListDPDLabelsResponse,
  ListDPDServicesResponse,
} from '../models/APIModels';
import Loading from './Loading';
import moment from 'moment';

interface DPDShippingModalProps {
  isOpen: boolean;
  close: () => void;
  collectionAddress: string;
  deliveryAddress: string;
  orderBoxes?: OrderBoxesResponse[];
  orderId: number;
  customer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  };
}

interface DPDFormValues {
  numberOfParcels: number;
  totalWeight: number;
  collectionDate?: Date;
  deliveryInstructions: string;
  shippingRef1: string;
  networkCode: string;
  collectionAddress: BackendShippingAddress;
  deliveryAddress: BackendShippingAddress;
  customCollectionAddress: Address;
  customDeliveryAddress: Address;
}

interface DPDFormErrors {
  numberOfParcels?: string;
  totalWeight?: string;
  collectionDate?: string;
  deliveryInstructions?: string;
  shippingRef1?: string;
  networkCode?: string;
  collectionAddress?: string;
  deliveryAddress?: string;
}

const DEFAULT_NETWORK_CODE = '2^12';

const UK_POSTCODE_REGEX = /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/;

const KIT_KEEPER_WAREHOUSE_ADDRESS: BackendShippingAddress = {
  contactName: 'Michael McCreadie',
  organisationName: 'KIT KEEPER LTD - ARDINGTON ARCHIVE',
  addressLine1: 'ARDINGTON ARCHIVE',
  addressLine2: 'WHITE HORSE BUSINESS PARK/WARE ROAD',
  town: 'FARINGDON',
  county: 'Oxfordshire',
  postcode: 'SN7 8NY',
  countryCode: 'GB',
  phone: '',
  email: 'warehouse@kitkeeper.co.uk',
};

const parseAddressString = (
  addressString: string,
): Partial<BackendShippingAddress> => {
  const parts = addressString.split(', ').filter((p) => p.trim());
  return {
    addressLine1: parts[0] || '',
    addressLine2: parts.length > 3 ? parts[1] : '',
    town: parts[parts.length - 3] || '',
    postcode: parts[parts.length - 2] || '',
    countryCode: 'GB',
  };
};

const countryCodeToName: Record<string, string> = {
  GB: 'United Kingdom',
  US: 'United States',
  IE: 'Ireland',
  FR: 'France',
  DE: 'Germany',
};

const addressToLookup = (addr: BackendShippingAddress): Address => ({
  line1: addr.addressLine1,
  line2: addr.addressLine2,
  city: addr.town,
  county: addr.county,
  postCode: addr.postcode,
  country: countryCodeToName[addr.countryCode] || addr.countryCode,
});

const toCountryCode = (country?: string): string => {
  if (!country) return 'GB';
  const normalized = country.trim().toUpperCase();
  if (normalized.length === 2) return normalized;
  if (normalized === 'UNITED KINGDOM' || normalized === 'UK' || normalized === 'GREAT BRITAIN') return 'GB';
  return 'GB';
};

const lookupToAddress = (
  lookup: Address,
  base: BackendShippingAddress,
): BackendShippingAddress => ({
  ...base,
  addressLine1: lookup.line1 || '',
  addressLine2: lookup.line2 || '',
  town: lookup.city || '',
  county: lookup.county || '',
  postcode: lookup.postCode || '',
  countryCode: toCountryCode(lookup.country),
});

const printDpdLabel = (labelHtml: string) => {
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(labelHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }
};

const customStyles = {
  content: {
    display: 'flex',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '24px',
  },
};

const RequiredDot = () => (
  <span className="dpd-required-dot" title="Required field">*</span>
);

const validateForm = (values: DPDFormValues): DPDFormErrors => {
  const errors: DPDFormErrors = {};

  if (!values.numberOfParcels || values.numberOfParcels < 1) {
    errors.numberOfParcels = 'Must be at least 1';
  } else if (values.numberOfParcels > 99) {
    errors.numberOfParcels = 'Must be 99 or fewer';
  }

  if (!values.totalWeight || values.totalWeight <= 0) {
    errors.totalWeight = 'Weight must be greater than 0';
  } else if (values.totalWeight > 99999) {
    errors.totalWeight = 'Weight must be 99999 or less';
  }

  if (!values.collectionDate) {
    errors.collectionDate = 'Please select a collection date';
  }

  if (!values.networkCode) {
    errors.networkCode = 'Please select a DPD service';
  }

  if (!values.collectionAddress.addressLine1) {
    errors.collectionAddress = 'Collection address line 1 is required';
  } else if (!values.collectionAddress.town) {
    errors.collectionAddress = 'Collection town is required';
  } else if (!values.collectionAddress.postcode) {
    errors.collectionAddress = 'Collection postcode is required';
  }

  if (!values.deliveryAddress.contactName) {
    errors.deliveryAddress = 'Delivery contact name is required';
  } else if (!values.deliveryAddress.addressLine1) {
    errors.deliveryAddress = 'Delivery address line 1 is required';
  } else if (!values.deliveryAddress.town) {
    errors.deliveryAddress = 'Delivery town is required';
  } else if (!values.deliveryAddress.postcode) {
    errors.deliveryAddress = 'Delivery postcode is required';
  } else if (!values.deliveryAddress.phone) {
    errors.deliveryAddress = 'Delivery phone is required';
  } else if (!values.deliveryAddress.email) {
    errors.deliveryAddress = 'Delivery email is required';
  }

  return errors;
};

export default function DPDShippingModal({
  isOpen,
  close,
  collectionAddress,
  deliveryAddress,
  orderBoxes,
  orderId,
  customer,
}: DPDShippingModalProps): JSX.Element {
  const [existingLabels, setExistingLabels] = useState<DPDLabel[]>([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [createdLabel, setCreatedLabel] = useState<DPDLabel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [availableServices, setAvailableServices] = useState<DPDService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [servicesFetched, setServicesFetched] = useState(false);
  const [servicesError, setServicesError] = useState(false);
  const lastFetchedPostcodesRef = useRef<string>('');

  const disabledDays = useDateDisabler(null, null, 1);

  const parsedDelivery = parseAddressString(deliveryAddress);
  const initialDeliveryAddress: BackendShippingAddress = {
    contactName:
      `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim() || '',
    organisationName: '',
    addressLine1: parsedDelivery.addressLine1 || '',
    addressLine2: parsedDelivery.addressLine2 || '',
    town: parsedDelivery.town || '',
    county: '',
    postcode: parsedDelivery.postcode || '',
    countryCode: 'GB',
    phone: customer?.phoneNumber || '',
    email: customer?.email || '',
  };

  const boxCount = orderBoxes?.length || 1;

  const initialValues: DPDFormValues = {
    numberOfParcels: boxCount,
    totalWeight: boxCount * 25,
    collectionDate: undefined,
    deliveryInstructions: '',
    shippingRef1: `ORDER-${orderId}`.substring(0, 25),
    networkCode: DEFAULT_NETWORK_CODE,
    collectionAddress: { ...KIT_KEEPER_WAREHOUSE_ADDRESS },
    deliveryAddress: initialDeliveryAddress,
    customCollectionAddress: addressToLookup(KIT_KEEPER_WAREHOUSE_ADDRESS),
    customDeliveryAddress: addressToLookup(initialDeliveryAddress),
  };

  useEffect(() => {
    if (isOpen) {
      setLoadingLabels(true);
      setError(null);
      setCreatedLabel(null);

      axiosKK
        .get<ListDPDLabelsResponse>(
          `/admin/order/${orderId}/shipping/labels`,
        )
        .then(({ data }) => {
          setExistingLabels(data.labels || []);
        })
        .catch((err) => {
          console.error('Failed to fetch existing labels:', err);
        })
        .finally(() => {
          setLoadingLabels(false);
        });
    }
  }, [isOpen, orderId]);

  const fetchServices = useCallback((collectionPostcode: string, deliveryPostcode: string) => {
    if (!collectionPostcode || !deliveryPostcode) return;

    const key = `${collectionPostcode}|${deliveryPostcode}`;
    if (key === lastFetchedPostcodesRef.current) return;
    lastFetchedPostcodesRef.current = key;

    setLoadingServices(true);
    setServicesError(false);
    axiosKK
      .get<ListDPDServicesResponse>('/admin/shipping/services', {
        params: { collectionPostcode, deliveryPostcode },
      })
      .then(({ data }) => {
        setAvailableServices(data.services || []);
        setServicesFetched(true);
      })
      .catch((err) => {
        console.error('Failed to fetch DPD services:', err);
        setAvailableServices([]);
        setServicesFetched(true);
        setServicesError(true);
      })
      .finally(() => {
        setLoadingServices(false);
      });
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchServices(
        KIT_KEEPER_WAREHOUSE_ADDRESS.postcode,
        initialDeliveryAddress.postcode,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePrintExistingLabel = (labelId: number) => {
    axiosKK
      .get<{ labelHtml: string }>(`/admin/shipping/label/${labelId}/print`)
      .then(({ data }) => {
        printDpdLabel(data.labelHtml);
      })
      .catch((err) => {
        console.error('Failed to fetch label for printing:', err);
        setError('Failed to fetch label for printing');
      });
  };

  const handleDeliveryAddressChange = (
    address: Address,
    collectionPostcode: string,
    setFieldValue: FormikProps<DPDFormValues>['setFieldValue'],
    currentDeliveryAddress: BackendShippingAddress,
  ) => {
    setFieldValue('customDeliveryAddress', address);
    const updated = lookupToAddress(address, currentDeliveryAddress);
    setFieldValue('deliveryAddress', updated);
    if (updated.postcode && UK_POSTCODE_REGEX.test(updated.postcode.trim())) {
      fetchServices(collectionPostcode, updated.postcode);
    }
  };

  const handleCollectionAddressChange = (
    address: Address,
    deliveryPostcode: string,
    setFieldValue: FormikProps<DPDFormValues>['setFieldValue'],
    currentCollectionAddress: BackendShippingAddress,
  ) => {
    setFieldValue('customCollectionAddress', address);
    const updated = lookupToAddress(address, currentCollectionAddress);
    setFieldValue('collectionAddress', updated);
    if (updated.postcode && UK_POSTCODE_REGEX.test(updated.postcode.trim())) {
      fetchServices(updated.postcode, deliveryPostcode);
    }
  };

  const renderServiceDropdown = (
    values: DPDFormValues,
    handleChange: FormikProps<DPDFormValues>['handleChange'],
  ) => {
    const deliveryPc = values.deliveryAddress.postcode?.trim() || '';
    const hasValidPostcode = UK_POSTCODE_REGEX.test(deliveryPc);

    if (loadingServices) {
      return (
        <div className="dpd-service-status loading">
          <Loading />
          <span>Loading available services for this address...</span>
        </div>
      );
    }

    if (!hasValidPostcode && !servicesFetched) {
      return (
        <div className="dpd-service-status info">
          Enter a valid delivery postcode below to see available DPD services.
        </div>
      );
    }

    if (servicesFetched && servicesError) {
      return (
        <div className="dpd-service-status warning">
          Could not load services for this address. Using default service.
          <select
            id="networkCode"
            name="networkCode"
            value={values.networkCode}
            onChange={handleChange}
            className="form-input dpd-service-select"
          >
            <option value={DEFAULT_NETWORK_CODE}>
              DPD Local Parcel Next Day (default)
            </option>
          </select>
        </div>
      );
    }

    if (servicesFetched && availableServices.length === 0) {
      return (
        <div className="dpd-service-status warning">
          No services available for this route. Check the delivery address postcode.
          <select
            id="networkCode"
            name="networkCode"
            value={values.networkCode}
            onChange={handleChange}
            className="form-input dpd-service-select"
          >
            <option value={DEFAULT_NETWORK_CODE}>
              DPD Local Parcel Next Day (default)
            </option>
          </select>
        </div>
      );
    }

    return (
      <select
        id="networkCode"
        name="networkCode"
        value={values.networkCode}
        onChange={handleChange}
        className="form-input dpd-service-select"
      >
        {availableServices.map((service) => (
          <option key={service.networkCode} value={service.networkCode}>
            {service.networkDescription} ({service.networkCode})
          </option>
        ))}
      </select>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      style={customStyles}
      contentLabel="DPD Shipping Modal"
      ariaHideApp={false}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validateOnMount
        validate={validateForm}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          setError(null);

          const formattedDate = moment(values.collectionDate).format(
            'YYYY-MM-DD',
          );
          const collectionDateStr = `${formattedDate}T09:00:00`;

          const requestData: CreateDPDLabelRequest = {
            orderId,
            collectionAddress: {
              ...values.collectionAddress,
              countryCode: toCountryCode(values.collectionAddress.countryCode),
            },
            deliveryAddress: {
              ...values.deliveryAddress,
              countryCode: toCountryCode(values.deliveryAddress.countryCode),
            },
            numberOfParcels: values.numberOfParcels,
            totalWeight: values.totalWeight,
            networkCode: values.networkCode,
            collectionDate: collectionDateStr,
            shippingRef1: values.shippingRef1,
            deliveryInstructions: values.deliveryInstructions,
          };

          axiosKK
            .post<CreateDPDLabelResponse>(
              '/admin/shipping/label',
              requestData,
            )
            .then(({ data }) => {
              setCreatedLabel(data.label);
              setExistingLabels((prev) => [data.label, ...prev]);
              setSubmitting(false);
            })
            .catch((err) => {
              console.error('Failed to create DPD label:', err);
              setError(
                err.response?.data?.error ||
                  'Failed to create DPD shipping label',
              );
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
        }) => {
          const hasErrors = Object.keys(errors).length > 0;
          const isFormDisabled = hasErrors || isSubmitting;

          return (
            <form onSubmit={handleSubmit} className="dpd-shipping-form">
              <div className="modal-header">
                <h3>DPD Shipping Label</h3>
                <button type="button" className="dpd-close-btn" onClick={close}>
                  &times;
                </button>
              </div>

              <div className="modal-content">
                {loadingLabels && (
                  <div className="loading-message">
                    <p>Loading existing labels...</p>
                    <Loading />
                  </div>
                )}

                {error && (
                  <div className="error-message">
                    <span className="error">{error}</span>
                  </div>
                )}

                {existingLabels.length > 0 && !createdLabel && (
                  <div className="existing-labels">
                    <h4>Existing Labels</h4>
                    <table className="labels-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Tracking #</th>
                          <th>Parcel #</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {existingLabels.map((label) => {
                          const isVoided = label.status === 'voided';
                          const canPrint = label.status === 'open' || label.status === 'created';
                          return (
                            <tr key={label.id} className={isVoided ? 'label-row--voided' : ''}>
                              <td>
                                {moment(label.createdAt).format('DD/MM/YYYY')}
                              </td>
                              <td>{label.trackingNumber}</td>
                              <td>{label.parcelNumber}</td>
                              <td>
                                <span className={`label-status label-status--${label.status}`}>
                                  {label.status}
                                </span>
                              </td>
                              <td className="label-actions-cell">
                                {canPrint && (
                                  <SubmitButton
                                    type="button"
                                    onClick={() => handlePrintExistingLabel(label.id)}
                                  >
                                    Print
                                  </SubmitButton>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="dpd-portal-notice">
                      To void or manage shipments, use the{' '}
                      <a
                        href="https://myshipments.dpdlocal.co.uk/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        DPD Shipments Portal
                      </a>
                    </div>
                  </div>
                )}

                {createdLabel && (
                  <div className="shipping-label-success">
                    <h4>DPD Label Created Successfully!</h4>
                    <div className="label-details">
                      <p>
                        <strong>Tracking Number:</strong>{' '}
                        {createdLabel.trackingNumber}
                      </p>
                      <p>
                        <strong>Parcel Number:</strong>{' '}
                        {createdLabel.parcelNumber}
                      </p>
                      <p>
                        <strong>Shipment ID:</strong> {createdLabel.shipmentId}
                      </p>
                    </div>
                    <div className="label-actions">
                      <SubmitButton
                        type="button"
                        onClick={() => {
                          if (createdLabel.labelData) {
                            printDpdLabel(createdLabel.labelData);
                          } else {
                            handlePrintExistingLabel(createdLabel.id);
                          }
                        }}
                      >
                        Print Label
                      </SubmitButton>
                    </div>
                  </div>
                )}

                {!createdLabel && !loadingLabels && (
                  <>
                    <div className="dpd-form-row">
                      <div className="form-group">
                        <Label htmlFor="numberOfParcels">Number of Parcels <RequiredDot /></Label>
                        <input
                          id="numberOfParcels"
                          name="numberOfParcels"
                          type="number"
                          min="1"
                          max="99"
                          value={values.numberOfParcels}
                          onChange={handleChange}
                          className={`form-input${touched.numberOfParcels && errors.numberOfParcels ? ' input-error' : ''}`}
                        />
                        {touched.numberOfParcels && errors.numberOfParcels && (
                          <span className="field-error">{errors.numberOfParcels}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <Label htmlFor="totalWeight">Total Weight (kg) <RequiredDot /></Label>
                        <input
                          id="totalWeight"
                          name="totalWeight"
                          type="number"
                          min="0.1"
                          step="0.1"
                          max="99999"
                          value={values.totalWeight}
                          onChange={handleChange}
                          className={`form-input${touched.totalWeight && errors.totalWeight ? ' input-error' : ''}`}
                        />
                        {touched.totalWeight && errors.totalWeight && (
                          <span className="field-error">{errors.totalWeight}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <div className="title-and-input">
                        <div className="title">
                          <Label>Collection Date <RequiredDot />:</Label>
                          <CalendarIcon
                            style={{
                              width: '24px',
                              height: '24px',
                              color: 'var(--kkUltraViolet)',
                            }}
                          />
                        </div>
                        <DayPickerInputValue
                          id="dpdCollectionDate"
                          handleStateChange={(day) =>
                            setFieldValue('collectionDate', day)
                          }
                          disabled={false}
                          disabledDays={disabledDays}
                        />
                      </div>
                      {touched.collectionDate && errors.collectionDate && (
                        <span className="field-error">{errors.collectionDate}</span>
                      )}
                    </div>

                    <div className="dpd-form-row">
                      <div className="form-group">
                        <Label htmlFor="shippingRef1">
                          Shipping Reference
                        </Label>
                        <input
                          id="shippingRef1"
                          name="shippingRef1"
                          type="text"
                          maxLength={25}
                          value={values.shippingRef1}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Max 25 characters"
                        />
                      </div>

                      <div className="form-group">
                        <Label htmlFor="deliveryInstructions">
                          Delivery Instructions
                        </Label>
                        <input
                          id="deliveryInstructions"
                          name="deliveryInstructions"
                          type="text"
                          maxLength={50}
                          value={values.deliveryInstructions}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Max 50 characters"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <Label htmlFor="networkCode">DPD Service <RequiredDot /></Label>
                      {renderServiceDropdown(values, handleChange)}
                    </div>

                    <div className="form-group address-section">
                      <h4>Collection Address (From)</h4>
                      <div className="address-fields">
                        <div className="address-field">
                          <Label htmlFor="collectionContactName">
                            Contact Name
                          </Label>
                          <input
                            id="collectionContactName"
                            type="text"
                            value={values.collectionAddress.contactName}
                            onChange={(e) =>
                              setFieldValue(
                                'collectionAddress.contactName',
                                e.target.value,
                              )
                            }
                            className="form-input"
                          />
                        </div>
                        <div className="address-field">
                          <Label htmlFor="collectionOrg">Organisation</Label>
                          <input
                            id="collectionOrg"
                            type="text"
                            value={values.collectionAddress.organisationName}
                            onChange={(e) =>
                              setFieldValue(
                                'collectionAddress.organisationName',
                                e.target.value,
                              )
                            }
                            className="form-input"
                          />
                        </div>
                        <div className="address-field">
                          <Label htmlFor="collectionPhone">Phone</Label>
                          <input
                            id="collectionPhone"
                            type="text"
                            value={values.collectionAddress.phone}
                            onChange={(e) =>
                              setFieldValue(
                                'collectionAddress.phone',
                                e.target.value,
                              )
                            }
                            className="form-input"
                          />
                        </div>
                        <div className="address-field">
                          <Label htmlFor="collectionEmail">Email</Label>
                          <input
                            id="collectionEmail"
                            type="text"
                            value={values.collectionAddress.email}
                            onChange={(e) =>
                              setFieldValue(
                                'collectionAddress.email',
                                e.target.value,
                              )
                            }
                            className="form-input"
                          />
                        </div>
                      </div>
                      <div className="custom-address-input">
                        <AddressLookup
                          address={values.customCollectionAddress}
                          setAddress={(address: Address) => {
                            handleCollectionAddressChange(
                              address,
                              values.deliveryAddress.postcode,
                              setFieldValue,
                              values.collectionAddress,
                            );
                          }}
                          onlyUnitedKingdom
                          idPrefix="dpd-from"
                        />
                      </div>
                      {touched.collectionAddress && errors.collectionAddress && (
                        <span className="field-error">{errors.collectionAddress}</span>
                      )}
                    </div>

                    <div className="form-group address-section">
                      <h4>Delivery Address (To)</h4>
                      <div className="address-fields">
                        <div className="address-field">
                          <Label htmlFor="deliveryContactName">
                            Contact Name <RequiredDot />
                          </Label>
                          <input
                            id="deliveryContactName"
                            type="text"
                            value={values.deliveryAddress.contactName}
                            onChange={(e) =>
                              setFieldValue(
                                'deliveryAddress.contactName',
                                e.target.value,
                              )
                            }
                            className="form-input"
                          />
                        </div>
                        <div className="address-field">
                          <Label htmlFor="deliveryOrg">Organisation</Label>
                          <input
                            id="deliveryOrg"
                            type="text"
                            value={values.deliveryAddress.organisationName}
                            onChange={(e) =>
                              setFieldValue(
                                'deliveryAddress.organisationName',
                                e.target.value,
                              )
                            }
                            className="form-input"
                          />
                        </div>
                        <div className="address-field">
                          <Label htmlFor="deliveryPhone">Phone <RequiredDot /></Label>
                          <input
                            id="deliveryPhone"
                            type="text"
                            value={values.deliveryAddress.phone}
                            onChange={(e) =>
                              setFieldValue(
                                'deliveryAddress.phone',
                                e.target.value,
                              )
                            }
                            className="form-input"
                          />
                        </div>
                        <div className="address-field">
                          <Label htmlFor="deliveryEmail">Email <RequiredDot /></Label>
                          <input
                            id="deliveryEmail"
                            type="text"
                            value={values.deliveryAddress.email}
                            onChange={(e) =>
                              setFieldValue(
                                'deliveryAddress.email',
                                e.target.value,
                              )
                            }
                            className="form-input"
                          />
                        </div>
                      </div>
                      <div className="custom-address-input">
                        <AddressLookup
                          address={values.customDeliveryAddress}
                          setAddress={(address: Address) => {
                            handleDeliveryAddressChange(
                              address,
                              values.collectionAddress.postcode,
                              setFieldValue,
                              values.deliveryAddress,
                            );
                          }}
                          onlyUnitedKingdom
                          idPrefix="dpd-to"
                        />
                      </div>
                      {touched.deliveryAddress && errors.deliveryAddress && (
                        <span className="field-error">{errors.deliveryAddress}</span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                {createdLabel ? (
                  <SubmitButton type="button" onClick={close}>
                    Done
                  </SubmitButton>
                ) : (
                  <div className="dpd-footer-content">
                    {hasErrors && !isSubmitting && (
                      <div className="dpd-validation-summary">
                        {Object.values(errors).map((msg, i) => (
                          <span key={i}>{msg}</span>
                        ))}
                      </div>
                    )}
                    <SubmitButton
                      disabled={isFormDisabled}
                      isSubmitting={isSubmitting}
                    >
                      {isSubmitting ? 'Creating...' : 'Create DPD Label'}
                    </SubmitButton>
                  </div>
                )}
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
}
